import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import toast, { Toaster } from "react-hot-toast";

import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

export default function Analytics() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [stats, setStats] = useState({});
  const [dailyData, setDailyData] = useState([]);
  const [guardPerformance, setGuardPerformance] = useState([]);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);
  const [incidentTrend, setIncidentTrend] = useState([]);
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchAnalytics(token);
  }, [timeRange]);

  const fetchDashboardData = async (token) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.data?.companyDetails) {
        setDashboardData(res.data.data.companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchAnalytics = async (token) => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await axios.get(
        `${API_BASE_URL}/reports/analytics?time_range=${timeRange}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setStats(res.data.stats || {});
      setDailyData(res.data.daily_data || []);
      setGuardPerformance(res.data.guard_performance || []);
      setHourlyDistribution(res.data.hourly_distribution || []);
      setIncidentTrend(res.data.incident_trend || []);
      
      toast.success("Analytics data loaded successfully!");
    } catch (err) {
      console.error("Analytics fetch error:", err);
      toast.error(err.response?.data?.message || "Failed to load analytics data.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const token = getToken();
    fetchAnalytics(token);
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  // Chart configurations
  const patrolTrendChart = {
    labels: dailyData.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: "Patrols",
        data: dailyData.map((d) => d.patrol_count),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59,130,246,0.2)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#3b82f6",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const guardChart = {
    labels: guardPerformance.slice(0, 10).map((d) => d.guard_name || `Guard ${d.guard_id}`),
    datasets: [
      {
        label: "Patrols Completed",
        data: guardPerformance.slice(0, 10).map((d) => d.patrol_count),
        backgroundColor: [
          "#3b82f6",
          "#10b981",
          "#f59e0b",
          "#8b5cf6",
          "#0ea5e9",
          "#14b8a6",
          "#f97316",
          "#ec4899",
          "#6366f1",
          "#06b6d4",
        ],
        borderRadius: 6,
        maxBarThickness: 40,
      },
    ],
  };

  const hourlyChart = {
    labels: hourlyDistribution.map((d) => `${d.hour}:00`),
    datasets: [
      {
        label: "Patrols by Hour",
        data: hourlyDistribution.map((d) => d.patrol_count),
        backgroundColor: "rgba(245, 158, 11, 0.7)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        borderRadius: 6,
        maxBarThickness: 40,
      },
    ],
  };

  const incidentTrendChart = {
    labels: incidentTrend.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: "Incidents",
        data: incidentTrend.map((d) => d.incident_count),
        borderColor: "#ef4444",
        backgroundColor: "rgba(239,68,68,0.2)",
        tension: 0.4,
        fill: true,
        pointBackgroundColor: "#ef4444",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };

  const patrolVsIncidentChart = {
    labels: ["Patrols", "Incidents"],
    datasets: [
      {
        data: [stats.total_patrols || 0, stats.total_incidents || 0],
        backgroundColor: ["#3b82f6", "#ef4444"],
        borderColor: ["#2563eb", "#dc2626"],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: "var(--text-primary)",
          font: { family: "Inter, system-ui, sans-serif" },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "var(--divider)",
        },
        ticks: {
          color: "var(--text-muted)",
          font: { family: "Inter, system-ui, sans-serif" },
        },
      },
      y: {
        grid: {
          color: "var(--divider)",
        },
        ticks: {
          color: "var(--text-muted)",
          font: { family: "Inter, system-ui, sans-serif" },
        },
        beginAtZero: true,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          color: "var(--text-primary)",
          font: { family: "Inter, system-ui, sans-serif", size: 13 },
          padding: 20,
        },
      },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading analytics...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="analytics"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Analytics Dashboard
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Visual insights of patrol performance, incidents, guards and trends
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Analytics Filters
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Filter analytics data by time range
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] min-w-[150px]"
                style={{ fontFamily: FONT_UI }}
              >
                <option value={7}>7 Days</option>
                <option value={30}>30 Days</option>
                <option value={60}>60 Days</option>
                <option value={90}>90 Days</option>
              </select>
            </div>

            <button
              onClick={handleRefresh}
              className="border-0 rounded-xl px-6 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-4">
            Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Total Guards" value={stats.total_guards || 0} color="blue" />
            <StatCard label="Total Patrols" value={stats.total_patrols || 0} color="green" />
            <StatCard label="Total Incidents" value={stats.total_incidents || 0} color="red" />
            <StatCard 
              label="Completion Rate" 
              value={`${stats.completion_rate || 0}%`} 
              color="amber" 
            />
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-4">
          <ChartCard
            title="Daily Patrol Trend"
            description="Shows how many patrol scans were completed each day."
          >
            <div className="h-[280px]">
              <Line data={patrolTrendChart} options={chartOptions} />
            </div>
          </ChartCard>

          <ChartCard
            title="Incident Trend"
            description="Shows incident volume during the selected period."
          >
            <div className="h-[280px]">
              <Line data={incidentTrendChart} options={chartOptions} />
            </div>
          </ChartCard>

          <ChartCard
            title="Top Guard Performance"
            description="Top 10 guards based on completed patrol scans."
          >
            <div className="h-[280px]">
              <Bar data={guardChart} options={chartOptions} />
            </div>
          </ChartCard>

          <ChartCard
            title="Hourly Patrol Distribution"
            description="Helps identify peak patrol activity hours."
          >
            <div className="h-[280px]">
              <Bar data={hourlyChart} options={chartOptions} />
            </div>
          </ChartCard>

          <ChartCard
            title="Patrols vs Incidents"
            description="Quick comparison between total patrol activity and incidents."
          >
            <div className="h-[280px] flex items-center justify-center">
              <div className="w-[250px] h-[250px]">
                <Doughnut data={patrolVsIncidentChart} options={doughnutOptions} />
              </div>
            </div>
          </ChartCard>

          <DetailsCard
            title="Analytics Summary"
            stats={stats}
            guardPerformance={guardPerformance}
            hourlyDistribution={hourlyDistribution}
          />
        </div>
      </div>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function ChartCard({ title, description, children }) {
  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
      <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1" style={{ fontFamily: FONT_UI }}>
        {description}
      </p>
      {children}
    </div>
  );
}

function DetailsCard({ title, stats, guardPerformance, hourlyDistribution }) {
  const topGuard = guardPerformance[0];
  const peakHour = hourlyDistribution.reduce(
    (max, item) => (item.patrol_count > max.patrol_count ? item : max),
    { patrol_count: 0 }
  );

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
      <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0 mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>

      <div className="space-y-3 text-[13px] text-[var(--text-primary)]" style={{ fontFamily: FONT_UI }}>
        <div className="flex justify-between items-center py-2 border-b border-[var(--divider)]">
          <span className="text-[var(--text-muted)]">Total patrols completed</span>
          <span className="font-extrabold">{stats.total_patrols || 0}</span>
        </div>

        <div className="flex justify-between items-center py-2 border-b border-[var(--divider)]">
          <span className="text-[var(--text-muted)]">Total incidents reported</span>
          <span className="font-extrabold">{stats.total_incidents || 0}</span>
        </div>

        <div className="flex justify-between items-center py-2 border-b border-[var(--divider)]">
          <span className="text-[var(--text-muted)]">Completion rate</span>
          <span className="font-extrabold text-[var(--accent)]">{stats.completion_rate || 0}%</span>
        </div>

        <div className="flex justify-between items-center py-2 border-b border-[var(--divider)]">
          <span className="text-[var(--text-muted)]">Best performing guard</span>
          <span className="font-extrabold text-[var(--accent)]">
            {topGuard ? topGuard.guard_name || `Guard ${topGuard.guard_id}` : "No data"}
          </span>
        </div>

        <div className="flex justify-between items-center py-2">
          <span className="text-[var(--text-muted)]">Peak patrol hour</span>
          <span className="font-extrabold text-[var(--accent)]">
            {peakHour.hour !== undefined ? `${peakHour.hour}:00` : "No data"}
          </span>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colorMap = {
    blue: "bg-[rgba(59,130,246,0.12)] text-[#3b82f6]",
    green: "bg-[rgba(16,185,129,0.12)] text-[#10b981]",
    red: "bg-[rgba(239,68,68,0.12)] text-[#ef4444]",
    amber: "bg-[rgba(245,158,11,0.12)] text-[#f59e0b]",
    purple: "bg-[rgba(139,92,246,0.12)] text-[#8b5cf6]",
  };
  return (
    <div className={`rounded-xl px-4 py-4 border ${colorMap[color] || colorMap.blue}`}>
      <p className="text-[10px] font-extrabold uppercase tracking-wider opacity-75 m-0" style={{ fontFamily: FONT_UI }}>
        {label}
      </p>
      <p className="text-[26px] font-extrabold m-0 mt-0.5" style={{ fontFamily: FONT_HEADING }}>
        {value}
      </p>
    </div>
  );
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}